import React from 'react';
import { createRoot } from 'react-dom/client';

import { getBuildoutAttributes } from './helpers/attributes';
import Link from './components/link';
import List from './components/list';
import Table from './components/table';
import Text from './components/text';

const rootsByNode = new WeakMap();

function renderInNode(node, element) {
  let root = rootsByNode.get(node);
  if (!root) {
    root = createRoot(node);
    rootsByNode.set(node, root);
  }
  root.render(element);
}

export function findNodesByComponentType(doc) {
  const nodes = {
    link: [],
    list: [],
    table: [],
    text: []
  };

  for (const node of doc.querySelectorAll('*[bo-slug]')) {
    if (node.tagName === 'OL' || node.tagName === 'UL') {
      nodes.list.push(node);
    } else if (node.tagName === 'TABLE' || node.tagName === 'TBODY') {
      nodes.table.push(node);
    } else if (node.closest('a')) {
      nodes.link.push(node);
    } else {
      nodes.text.push(node);
    }
  }

  return nodes;
}

const renderFunctionsByType = {
  link: renderLinkComponent,
  list: renderListComponent,
  table: renderTableComponent,
  text: renderTextComponent
};

export function renderComponents(nodesByComponentType, getProps, getState) {
  for (const type in nodesByComponentType) {
    const nodes = nodesByComponentType[type];

    nodes.forEach((node, i) => {
      // Render functions may replace DOM nodes and return the new node, so hang onto the result
      nodes[i] = renderFunctionsByType[type](node, getProps, getState);
    });
  }
}

export function unmountComponents(nodesByComponentType) {
  try {
    for (const type in nodesByComponentType) {
      for (const node of nodesByComponentType[type]) {
        const root = rootsByNode.get(node);
        if (root) {
          rootsByNode.delete(node);
          queueMicrotask(() => root.unmount());
        }
      }
    }
  } catch (e) {
    // Edge throws 'Permission Denied' when accessing the DOM nodes in nodesByComponentType.
    // Just ignore it - leaving things mounted on nonexistent nodes doesn't break anything, it only
    // leaves the React dev tools dirty and causes a (probably) inconcequential memory leak.
  }
}

// Hang onto some things for rerendering to avoid hitting the DOM for every rerender
// Be aware that some actions (e.g. resetting all overrides) may trigger a render without these
function getStaticComponentProps(node, getProps, getState, renderFunction) {
  const id = node.getAttribute('bo-slug');

  const staticProps = {
    id,
    aiPromptKey: node.getAttribute('bo-ai-prompt-key'),
    sanitize: node.getAttribute('bo-sanitize') !== 'false',

    rerender() {
      renderFunction(node, getProps, getState, staticProps);
    },

    onAiInteraction() {
      getProps().onAiInteraction(staticProps.aiPromptKey, staticProps.onOverrideChange);
    },

    onOverrideChange(override) {
      getProps().onOverridesChange({ [id]: override }, staticProps.rerender);
    },

    onOverrideReset() {
      getProps().onOverridesReset([id], staticProps.rerender);
    }
  };

  return staticProps;
}

function getStaticTableComponentProps(node, getProps, getState) {
  const staticProps = getStaticComponentProps(node, getProps, getState, renderTableComponent);
  staticProps.allowColumnEditing = node.getAttribute('bo-allow-column-editing') !== 'false';
  staticProps.allowRowEditing = node.getAttribute('bo-allow-row-editing') !== 'false';
  staticProps.newColumnClass = node.getAttribute('bo-new-column-class');
  staticProps.newColumnStyle = node.getAttribute('bo-new-column-style');
  staticProps.newRowClass = node.getAttribute('bo-new-row-class');
  staticProps.newRowStyle = node.getAttribute('bo-new-row-style');
  return staticProps;
}

function getStaticTextComponentProps(node, getProps, getState, renderFunction) {
  const staticProps = getStaticComponentProps(node, getProps, getState, renderFunction);
  staticProps.defaultHtml = node.textContent.trim();
  staticProps.parentTagName = node.tagName.toLowerCase();
  return staticProps;
}

function replaceSluggedNode(node, attributesSource = node) {
  const div = document.createElement('div');
  node.parentNode.replaceChild(div, node);

  // Keep bo- attributes on the new div so getStaticComponentProps can read them on rerenders
  const attributes = getBuildoutAttributes(attributesSource);
  for (const name in attributes) {
    div.setAttribute(name, attributes[name]);
  }

  return div;
}

function renderListComponent(node, getProps, getState, staticProps) {
  const originalNode = node;

  if (node.tagName !== 'DIV') {
    // Need to render multiple things in the target node (e.g. the editable component header),
    // but lists can only have li elements as children. So, replace the original element with a div.
    node = replaceSluggedNode(originalNode);
  }

  if (!staticProps) {
    staticProps = getStaticComponentProps(node, getProps, getState, renderListComponent);
  }

  renderInNode(
    node,
    <List
      aiPromptKey={staticProps.aiPromptKey}
      defaultNode={originalNode}
      highlight={getState().highlightAll}
      override={getProps().overrides[staticProps.id]}
      sanitize={staticProps.sanitize}
      onAiInteraction={staticProps.onAiInteraction}
      onOverrideChange={staticProps.onOverrideChange}
      onOverrideReset={staticProps.onOverrideReset}
    />
  );

  return node;
}

function renderLinkComponent(node, getProps, getState, staticProps) {
  if (!staticProps) {
    staticProps = getStaticTextComponentProps(node, getProps, getState, renderLinkComponent);

    const link = node.closest('a');
    link.onclick = (event) => {
      if (event.target.getAttribute('name') !== 'link-preview') {
        event.preventDefault();
      }
    };
  }

  renderInNode(
    node,
    <Link
      defaultHtml={staticProps.defaultHtml}
      highlight={getState().highlightAll}
      override={getProps().overrides[staticProps.id]}
      parentTagName={staticProps.parentTagName}
      sanitize={staticProps.sanitize}
      onOverrideChange={staticProps.onOverrideChange}
      onOverrideReset={staticProps.onOverrideReset}
    />
  );

  return node;
}

function renderTableComponent(node, getProps, getState, staticProps) {
  const originalNode = node;
  let table = {};

  if (node.tagName !== 'DIV') {
    // For MJML templates, bo-slug attributes can't be put on mj-table elements and are instead put
    // on tbody elements. Make sure the table itself is always replaced, not the tbody, and that any
    // attributes are pulled from the original node.
    table = originalNode.closest('table');

    // Need to render multiple things in the target node (e.g. the editable component header), but
    // tables can't have divs as direct children. So, replace the original element with a div.
    node = replaceSluggedNode(table, originalNode);
  }

  if (!staticProps) {
    staticProps = getStaticTableComponentProps(node, getProps, getState);
  }

  renderInNode(
    node,
    <Table
      allowColumnEditing={staticProps.allowColumnEditing}
      allowRowEditing={staticProps.allowRowEditing}
      defaultNode={table}
      highlight={getState().highlightAll}
      newColumnClass={staticProps.newColumnClass}
      newColumnStyle={staticProps.newColumnStyle}
      newRowClass={staticProps.newRowClass}
      newRowStyle={staticProps.newRowStyle}
      override={getProps().overrides[staticProps.id]}
      sanitize={staticProps.sanitize}
      onOverrideChange={staticProps.onOverrideChange}
      onOverrideReset={staticProps.onOverrideReset}
    />
  );

  return node;
}

function renderTextComponent(node, getProps, getState, staticProps) {
  staticProps = staticProps || getStaticTextComponentProps(node, getProps, getState, renderTextComponent);

  renderInNode(
    node,
    <Text
      aiPromptKey={staticProps.aiPromptKey}
      defaultHtml={staticProps.defaultHtml}
      highlight={getState().highlightAll}
      override={getProps().overrides[staticProps.id]}
      parentTagName={staticProps.parentTagName}
      sanitize={staticProps.sanitize}
      onAiInteraction={staticProps.onAiInteraction}
      onOverrideChange={staticProps.onOverrideChange}
      onOverrideReset={staticProps.onOverrideReset}
    />
  );

  return node;
}
